import React from "react";
import { Phone, MapPin, Facebook, Instagram, Linkedin, ArrowUp } from "lucide-react";
import { businessInfo, navLinks, services } from "../data/mockData";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-gray-50 text-brand-charcoal border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src={businessInfo.logoImage}
              alt={`${businessInfo.name} logo`}
              className="h-28 w-auto object-contain mb-5"
              loading="lazy"
              decoding="async"
              data-testid="footer-logo"
            />
            <div className="inline-flex items-center gap-2 text-xs font-bold text-brand-charcoal">
              <span className="text-brand-gold-dark">★ {businessInfo.rating.toFixed(1)}</span>
              <span className="text-gray-300">·</span>
              <span className="text-gray-500">{businessInfo.reviewCount} Google Reviews</span>
            </div>
            {(businessInfo.facebook ||
              businessInfo.instagram ||
              businessInfo.linkedin) && (
              <div className="mt-6 flex gap-2">
                {businessInfo.facebook && (
                  <a
                    href={businessInfo.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-brand-charcoal hover:bg-brand-charcoal hover:border-brand-charcoal hover:text-white transition-all duration-200"
                    aria-label="Facebook"
                    data-testid="footer-facebook"
                  >
                    <Facebook className="w-3.5 h-3.5" />
                  </a>
                )}
                {businessInfo.instagram && (
                  <a
                    href={businessInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-brand-charcoal hover:bg-brand-charcoal hover:border-brand-charcoal hover:text-white transition-all duration-200"
                    aria-label="Instagram"
                    data-testid="footer-instagram"
                  >
                    <Instagram className="w-3.5 h-3.5" />
                  </a>
                )}
                {businessInfo.linkedin && (
                  <a
                    href={businessInfo.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-brand-charcoal hover:bg-brand-charcoal hover:border-brand-charcoal hover:text-white transition-all duration-200"
                    aria-label="LinkedIn"
                    data-testid="footer-linkedin"
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-brand-charcoal mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-gray-600 hover:text-brand-mint transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-brand-charcoal mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s.id}>
                  <span className="text-gray-600 text-sm">{s.title}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-brand-charcoal mb-6">Contact Info</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone className="w-3.5 h-3.5 text-brand-mint-dark mt-1 flex-shrink-0" />
                <a href={`tel:${businessInfo.phone}`} className="text-gray-600 hover:text-brand-mint transition-colors text-sm">
                  {businessInfo.phone}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-3.5 h-3.5 text-brand-mint-dark mt-1 flex-shrink-0" />
                <p className="text-gray-600 text-sm">{businessInfo.area}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-xs">
            © {new Date().getFullYear()} {businessInfo.name}. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="w-9 h-9 rounded-full bg-brand-gold hover:bg-brand-gold-light flex items-center justify-center text-brand-charcoal transition-all duration-200 hover:-translate-y-0.5 shadow-md"
            aria-label="Back to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
